import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import {
  PrescriptionPrintLayout,
  type PrintDoctor,
  type PrintPatient,
  type PrintPrescriptionItem,
} from '../pages/clinic/prescription-print/PrescriptionPrintLayout';
import type { PrescriptionPrintSettingsUI } from '@helios/data-access';
import { renderNodeToPng } from '../lib/prescription-image';
import { useSharePrescription } from '../hooks/useSharePrescription';

/* ------------------------------------------------------------------ */
/*  SharePrescriptionModal (PRD.238)                                  */
/*                                                                    */
/*  Confirms the WhatsApp recipient + optional caption, renders the   */
/*  existing PrescriptionPrintLayout to a PNG in a hidden offscreen   */
/*  container, and POSTs it to the prescription-share Cloud Function.*/
/*                                                                    */
/*  The hidden container is mounted inside this modal (not the parent */
/*  tab) so the image generation is decoupled from the print path —   */
/*  the existing `print()` flow keeps working unchanged.              */
/* ------------------------------------------------------------------ */

export interface SharePrescriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  patientName: string;
  /** Patient's WhatsApp number for display (free-text, e.g. "+63 9778310963"). */
  patientWhatsAppNumber: string | null;
  encounterId?: string;
  prescriptionIds?: string[];
  /** Pass-through to PrescriptionPrintLayout. */
  printSettings: PrescriptionPrintSettingsUI;
  printDoctor: PrintDoctor;
  printPatient: PrintPatient;
  printItems: PrintPrescriptionItem[];
  /** Brand accent (forwarded to the layout). */
  accentColor?: string;
}

export function SharePrescriptionModal({
  open,
  onOpenChange,
  patientId,
  patientName,
  patientWhatsAppNumber,
  encounterId,
  prescriptionIds,
  printSettings,
  printDoctor,
  printPatient,
  printItems,
  accentColor,
}: SharePrescriptionModalProps) {
  const { status, error, send, setRendering, reset, enabled } = useSharePrescription();

  const [caption, setCaption] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hiddenRef = useRef<HTMLDivElement | null>(null);

  // Reset transient state every time the modal opens.
  useEffect(() => {
    if (!open) return;
    reset();
    setCaption('');
  }, [open, reset]);

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }, []);

  const handleSend = useCallback(async () => {
    if (status === 'rendering' || status === 'sending') return; // dedupe
    if (!hiddenRef.current) {
      // Layout hasn't mounted yet — extremely unlikely since we render it
      // inline with the modal. Bail safely.
      return;
    }
    setRendering();
    let png;
    try {
      png = await renderNodeToPng({ node: hiddenRef.current });
    } catch (err) {
      console.error('[share-prescription] image render failed', err);
      showToast('We could not generate the prescription image. Please try again.');
      reset();
      return;
    }
    if (!png.base64) {
      showToast('We could not generate the prescription image. Please try again.');
      reset();
      return;
    }
    const result = await send({
      patientId,
      encounterId,
      prescriptionIds,
      imageBase64: png.base64,
      mimeType: 'image/png',
      caption: caption.trim() || undefined,
      clientReference: `encounter:${encounterId ?? 'unknown'}:prescription-share:${Date.now()}`,
    });
    if (result?.accepted) {
      onOpenChange(false);
      showToast(`Prescription sent to ${patientName} via WhatsApp.`);
    }
    // On failure the modal stays open and shows the inline error.
  }, [
    status,
    setRendering,
    send,
    patientId,
    encounterId,
    prescriptionIds,
    caption,
    onOpenChange,
    patientName,
    reset,
    showToast,
  ]);

  const disabled =
    !enabled ||
    !patientWhatsAppNumber ||
    printItems.length === 0 ||
    status === 'rendering' ||
    status === 'sending';

  const blockReason: string | null = !enabled
    ? 'WhatsApp messaging is not configured for this clinic.'
    : !patientWhatsAppNumber
      ? 'No WhatsApp number is available for this patient.'
      : printItems.length === 0
        ? 'Add at least one prescription item before sharing.'
        : null;

  return (
    <>
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title="Share Prescription via WhatsApp"
        description="Sends the prescription as an image inside the WhatsApp 24-hour customer service window."
        maxWidth={480}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <Button
              variant="ghost"
              size="md"
              onClick={() => onOpenChange(false)}
              disabled={status === 'rendering' || status === 'sending'}
            >
              Cancel
            </Button>
            <Button
              variant="gradient"
              size="md"
              onClick={handleSend}
              disabled={disabled}
              title={blockReason ?? undefined}
            >
              {status === 'rendering'
                ? 'Preparing image…'
                : status === 'sending'
                  ? 'Sending…'
                  : 'Send Prescription'}
            </Button>
          </div>
        }
      >
        <div style={modalBodyStyle}>
          <SummaryBox patientName={patientName} whatsAppNumber={patientWhatsAppNumber ?? '—'} />

          <div>
            <label style={fieldLabelStyle}>Format</label>
            <div
              style={{
                padding: 'var(--sp-2) var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--bg-surface-2)',
                border: '1px solid var(--border)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span>Image (PNG)</span>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                PDF coming soon
              </span>
            </div>
          </div>

          <div>
            <label htmlFor="prescription-share-caption" style={fieldLabelStyle}>
              Optional message
            </label>
            <textarea
              id="prescription-share-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={3}
              maxLength={1024}
              placeholder="Add a short note to send with the prescription (optional)…"
              style={textareaStyle}
            />
            <div
              style={{
                marginTop: 4,
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                textAlign: 'right',
              }}
            >
              {caption.trim().length} / 1024
            </div>
          </div>

          {blockReason && status === 'idle' && <div style={infoBoxStyle}>{blockReason}</div>}
          {status === 'error' && error && <div style={errorBoxStyle}>{error}</div>}
        </div>
      </Modal>

      {/* Hidden offscreen render of the printable prescription. html2canvas
          captures this node into a PNG on Send. Sized to A4-portrait width
          (210mm) so the output matches the print path's layout. */}
      <div
        ref={hiddenRef}
        aria-hidden
        style={{
          position: 'fixed',
          left: '-99999px',
          top: 0,
          width: '210mm',
          background: '#ffffff',
          pointerEvents: 'none',
        }}
      >
        <PrescriptionPrintLayout
          settings={printSettings}
          doctor={printDoctor}
          patient={printPatient}
          date={new Date().toISOString()}
          items={printItems}
          accentColor={accentColor}
        />
      </div>

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 'var(--sp-6)',
            right: 'var(--sp-6)',
            zIndex: 1000,
            padding: 'var(--sp-3) var(--sp-4)',
            borderRadius: 'var(--radius-md)',
            background: '#dcfce7',
            color: '#15803d',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function SummaryBox({
  patientName,
  whatsAppNumber,
}: {
  patientName: string;
  whatsAppNumber: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
        padding: 'var(--sp-3) var(--sp-4)',
        background: 'var(--bg-surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <SummaryRow label="Patient" value={patientName} />
      <SummaryRow label="WhatsApp" value={whatsAppNumber} />
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-4)' }}>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>{label}</span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text)',
          textAlign: 'right',
        }}
      >
        {value}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Styles                                                            */
/* ------------------------------------------------------------------ */

const modalBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-4)',
  padding: 'var(--sp-5, 20px) var(--sp-6, 24px)',
};

const fieldLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'var(--text-muted)',
  marginBottom: 'var(--sp-2)',
};

const textareaStyle: CSSProperties = {
  width: '100%',
  padding: 'var(--sp-3) var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-base)',
  color: 'var(--input-text)',
  fontFamily: 'inherit',
  resize: 'vertical',
  boxSizing: 'border-box',
};

const infoBoxStyle: CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  borderRadius: 'var(--radius-md)',
  background: '#f1f5f9',
  color: '#475569',
  fontSize: 'var(--font-size-sm)',
};

const errorBoxStyle: CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  borderRadius: 'var(--radius-md)',
  background: '#fef2f2',
  color: '#dc2626',
  fontSize: 'var(--font-size-sm)',
};
